/* eslint-disable no-console */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import { useIsProjectTeamsEnabled } from ':cloud/hooks/featureFlags/useIsProjectTeamsEnabled';
import { QueryKey } from ':cloud/queries/types';
import { useGetUser } from ':cloud/queries/UserQueries/useGetUser';
import { APIClientBase } from ':cloud/state/Api';
import { ProjectRoleNEW, ProjectsForUserNEW, ProjectWithRole } from ':cloud/types/service_proto';

async function fetchProjects(organizationId?: string): Promise<ProjectsForUserNEW> {
  const path = `/organization-management/v1/organizations/${organizationId}/projects`;
  const response = await APIClientBase.get<ProjectsForUserNEW>(path);
  return response?.data || [];
}

async function fetchProjectsNEW(): Promise<ProjectsForUserNEW> {
  const path = '/organization-management/v1/customers/projects';
  const response = await APIClientBase.get<ProjectsForUserNEW>(path);
  return response?.data || [];
}

type GenerateProjectQueryProps = {
  organizationId?: string;
};

function generateProjectsQuery({ organizationId }: GenerateProjectQueryProps) {
  return {
    queryKey: [QueryKey.projects, organizationId],
    queryFn: async () => fetchProjects(organizationId),
    staleTime: Infinity,
    enabled: !!organizationId,
  };
}

function generateProjectsQueryNEW(enabled: boolean) {
  return {
    queryKey: [QueryKey.projects],
    queryFn: async () => fetchProjectsNEW(),
    staleTime: Infinity,
    enabled,
  };
}

export function useGetProjects(organizationId?: string): {
  isLoading: boolean;
  projects: ProjectWithRole[];
  oldestProjectCreatedByUser: string | undefined; // timestamp or undefined
} {
  const { user } = useGetUser();
  const isProjectTeamsEnabled = useIsProjectTeamsEnabled();

  const { isLoading, data, isSuccess } = useQuery(generateProjectsQuery({ organizationId }));

  const newProjectsQuery = {
    ...generateProjectsQueryNEW(isProjectTeamsEnabled),
    enabled: isProjectTeamsEnabled && isSuccess,
  };

  const {
    isLoading: isLoadingNew,
    data: dataNew,
    isError: isErrorNew,
  } = useQuery(newProjectsQuery);

  const projectsWithUserRole = useMemo(() => {
    // Use new data only if:
    // 1. Feature flag is enabled
    // 2. New query has resolved successfully
    // 3. Original query has also finished loading (for safety)
    const shouldUseNewData = isProjectTeamsEnabled && !isErrorNew && !isLoadingNew && !isLoading;

    if (isProjectTeamsEnabled && isErrorNew && user.email.endsWith('@coinbase.com')) {
      console.error(
        '🚨 Project Teams API Failed - Falling back to legacy endpoint 🚨',
        'This should be investigated if seen frequently in production',
      );
    }

    const projectsData = shouldUseNewData ? dataNew : data;

    return (
      projectsData?.projects
        .filter((project) => project.active)
        ?.map((project) => ({
          ...project,
          userRole:
            project.users?.find((u) => u.id === user.userId)?.role ?? ProjectRoleNEW.Unspecified,
        }))
        ?.sort(
          (a, b) =>
            new Date(String(b.createdAt)).getTime() - new Date(String(a.createdAt)).getTime(),
        ) || []
    );
  }, [
    isProjectTeamsEnabled,
    isErrorNew,
    isLoadingNew,
    isLoading,
    user.email,
    user.userId,
    dataNew,
    data,
  ]);

  // We're loading if either query is loading
  const isLoadingEither = isLoading || isLoadingNew;

  const oldestProjectCreatedByUser = useMemo(() => {
    return projectsWithUserRole
      .filter((p) => p.createdByUserId === user.userId)
      .sort(
        (a, b) => new Date(String(a.createdAt)).getTime() - new Date(String(b.createdAt)).getTime(),
      )[0]?.createdAt;
  }, [projectsWithUserRole, user.userId]);

  return {
    isLoading: isProjectTeamsEnabled ? isLoadingEither : isLoading,
    projects: projectsWithUserRole,
    oldestProjectCreatedByUser,
  };
}
